import { useCallback, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';

import { FeedContext } from '../FeedContext.ts';

export type FireFeedCardAnalyticsEventParams = {
	type: 'sendTrackEvent' | 'sendUIEvent' | 'sendOperationalEvent';
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	commentId?: string;
	contentId?: string;
	additionalAttributes?: Record<string, any>;
	includeNavdex?: boolean;
};

export type FireFeedCardAnalyticsEvent = (params: FireFeedCardAnalyticsEventParams) => void;

type GetStatsObjectAndContainerResult = {
	containerType: string;
	containerId: string;
	objectType: string;
	objectId: string;
};
const getStatsObjectAndContainerAttributes = (
	contentId: string,
	spaceId: string,
	commentId?: string,
): GetStatsObjectAndContainerResult => {
	if (commentId) {
		return {
			containerType: 'page',
			containerId: contentId,
			objectType: 'comment',
			objectId: commentId,
		};
	}
	return {
		containerType: 'space',
		containerId: spaceId,
		objectType: 'page',
		objectId: contentId,
	};
};

export const useFeedCardAnalyticsEvents = (
	contentId: string,
	spaceId: string,
	actionSubjectIdPrefix: string,
	baseAttributes: Record<string, any>,
): FireFeedCardAnalyticsEvent => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const feedContext = useContext(FeedContext);

	return useCallback(
		({
			type,
			action,
			actionSubject,
			actionSubjectId,
			commentId,
			additionalAttributes = {},
			includeNavdex = false,
		}: any) => {
			const objectAndContainer = getStatsObjectAndContainerAttributes(
				contentId,
				spaceId,
				commentId,
			);

			const getActionSubjectId = (): string => {
				if (actionSubjectId) {
					return `${actionSubjectIdPrefix}-${actionSubjectId}`;
				}
				return actionSubjectIdPrefix;
			};

			createAnalyticsEvent({
				type,
				data: {
					source: 'homeScreen',
					action,
					actionSubject,
					actionSubjectId: getActionSubjectId(),
					...objectAndContainer,
					attributes: {
						...baseAttributes,
						...additionalAttributes,
						...(includeNavdex && {
							navdexPointType: START_TOUCH,
						}),
						feedSessionId: feedContext.feedSessionId,
					},
				},
			}).fire();
		},
		[
			contentId,
			spaceId,
			baseAttributes,
			actionSubjectIdPrefix,
			createAnalyticsEvent,
			feedContext.feedSessionId,
		],
	);
};
